/**
 * Password-setup form for invitation / first login.
 */
import * as React from 'react';
import { Input } from '@/components/forms/Input';
import { Button } from '@/components/forms/Button';
import { validatePassword, type PasswordRule } from '@/utils/auth-validation-utils';
import { Mail, Lock, Check, X, ShieldCheck, KeyRound } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { SETUP_PASSWORD_COPY, type SetupPasswordMode } from '@/components/auth/auth-configs';
import {
  getConfirmPasswordFieldError,
  getConfirmPasswordFieldState,
  getEmailFieldError,
  getEmailFieldState,
  getSetupPasswordFieldError,
  getSetupPasswordFieldState,
} from '@/components/auth/auth-utils';

/** Props for {@link SetupPasswordForm}. */
interface SetupPasswordFormProps {
  logo?: string;
  email: string;
  password: string;
  confirmPassword: string;
  emailTouched: boolean;
  passwordTouched: boolean;
  confirmPasswordTouched: boolean;
  isLoading: boolean;
  error?: string;
  isEmailDisabled: boolean;
  mode?: SetupPasswordMode;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onEmailBlur: () => void;
  onPasswordBlur: () => void;
  onConfirmPasswordBlur: () => void;
  onSubmit: React.SubmitEventHandler<HTMLFormElement>;
}

/** One password-criteria row with a check or cross icon. */
function PasswordRuleRow({ rule }: Readonly<{ rule: PasswordRule }>) {
  const color = rule.valid ? 'var(--color-success)' : 'var(--color-muted-foreground)';
  const Icon = rule.valid ? Check : X;

  return (
    <div className="flex items-center gap-2 text-sm" style={{ color }}>
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span>{rule.label}</span>
    </div>
  );
}

/** Live checklist shown after the password field is touched. */
function PasswordCriteriaList({ rules }: Readonly<{ rules: PasswordRule[] }>) {
  return (
    <div className="mt-3 space-y-2">
      <p className="text-sm font-medium text-muted-foreground">Critères de sécurité :</p>
      {rules.map(renderPasswordRuleRow)}
    </div>
  );
}

/** Render one {@link PasswordRuleRow} for the setup-password checklist. */
function renderPasswordRuleRow(rule: PasswordRule): React.ReactNode {
  return <PasswordRuleRow key={rule.key} rule={rule} />;
}

/** Invitation / first-login form: email, password, confirm, and criteria list. */
export const SetupPasswordForm: React.FC<SetupPasswordFormProps> = ({
  logo,
  email,
  password,
  confirmPassword,
  emailTouched,
  passwordTouched,
  confirmPasswordTouched,
  isLoading,
  error,
  isEmailDisabled,
  mode = 'activate',
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onEmailBlur,
  onPasswordBlur,
  onConfirmPasswordBlur,
  onSubmit,
}) => {
  const passwordValidation = validatePassword(password);
  const { logoAlt } = useTheme();
  const copy = SETUP_PASSWORD_COPY[mode];

  function handleEmailInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onEmailChange(e.target.value);
  }

  function handlePasswordInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onPasswordChange(e.target.value);
  }

  function handleConfirmPasswordInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onConfirmPasswordChange(e.target.value);
  }

  function renderLogo(): React.ReactNode {
    if (!logo) return null;
    return (
      <div className="login-logo-container">
        <img src={logo} alt={logoAlt} className="login-logo-main" />
      </div>
    );
  }

  function renderPasswordCriteria(): React.ReactNode {
    if (!passwordTouched) return null;
    return <PasswordCriteriaList rules={passwordValidation.rules} />;
  }

  function renderSubmitButton(): React.ReactNode {
    if (mode === 'reset') {
      return (
        <Button
          type="submit"
          loading={isLoading}
          disabled={isLoading || !email || !password || !confirmPassword}
          fullWidth
          variant="primary"
          size="lg"
          className="login-button"
          icon={KeyRound}
          iconPosition="left"
        >
          Réinitialiser le mot de passe
        </Button>
      );
    }
    return (
      <Button
        type="submit"
        loading={isLoading}
        disabled={isLoading || !email || !password || !confirmPassword}
        fullWidth
        variant="primary"
        size="lg"
        className="login-button"
        icon={ShieldCheck}
        iconPosition="left"
      >
        Activer mon compte
      </Button>
    );
  }

  return (
    <div className="setup-password-view">
      {renderLogo()}
      <h1 className="login-card-title">{copy.title}</h1>
      <p className="login-card-subtitle">{copy.subtitle}</p>

      <form onSubmit={onSubmit} className="login-form">
        <div className="login-field">
          <Input
            type="email"
            label="Adresse email"
            placeholder="votre@email.com"
            value={email}
            onChange={handleEmailInputChange}
            onBlur={onEmailBlur}
            icon={Mail}
            iconPosition="left"
            state={getEmailFieldState(email, emailTouched)}
            error={getEmailFieldError(email, emailTouched)}
            disabled={isEmailDisabled || isLoading}
            required
            autoFocus={!isEmailDisabled}
          />
        </div>

        <div className="login-field">
          <Input
            type="password"
            label="Mot de passe"
            placeholder="Créez un mot de passe"
            value={password}
            onChange={handlePasswordInputChange}
            onBlur={onPasswordBlur}
            icon={Lock}
            iconPosition="left"
            state={getSetupPasswordFieldState(passwordValidation.valid, passwordTouched)}
            error={getSetupPasswordFieldError(passwordValidation.valid, passwordTouched)}
            disabled={isLoading}
            required
            autoFocus={isEmailDisabled}
            showPasswordToggle
          />

          {renderPasswordCriteria()}
        </div>

        <div className="login-field">
          <Input
            type="password"
            label="Confirmer le mot de passe"
            placeholder="Confirmez votre mot de passe"
            value={confirmPassword}
            onChange={handleConfirmPasswordInputChange}
            onBlur={onConfirmPasswordBlur}
            icon={Lock}
            iconPosition="left"
            state={getConfirmPasswordFieldState(
              confirmPassword,
              password,
              passwordValidation.valid,
              confirmPasswordTouched,
            )}
            error={getConfirmPasswordFieldError(
              confirmPassword,
              password,
              passwordValidation.valid,
              confirmPasswordTouched,
              error,
            )}
            disabled={isLoading}
            required
            showPasswordToggle
          />
        </div>

        <div className="login-field">{renderSubmitButton()}</div>

        <div className="mt-6 text-center">
          <p className="login-footer-legal">{copy.footer}</p>
        </div>
      </form>
    </div>
  );
};
